import { APP_PAGES, PAGES } from "@dune/types";
import { Button } from "@dune/ui";
import { ArrowLeft, ArrowRight } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetConstructionSiteCommunications,
	useGetConstructionSiteDetails,
	useGetConstructionSiteTasks,
	useGetConstructionSiteUnprocessedCommunicationsCount,
} from "../../api/queries/constructionSite.queries";
import { useGetConstructionSiteDocuments } from "../../api/queries/constructionSites.queries";
import { useMe } from "../../api/queries/users.queries";
import ErrorBoundary from "../../component/common/ErrorBoundary";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { useRequiredParams } from "../../hooks/useRequiredParams";
import CommunicationsTab from "./components/tabs/CommunicationsTab";
import DocumentsTab from "./components/tabs/DocumentsTab";
import GeneralTab from "./components/tabs/GeneralTab";
import ParticipantsTab from "./components/tabs/IntervenantsTab";
import { OperationTab } from "./components/tabs/OperationTab";
import TarificationTab from "./components/tabs/TarificationTab";
import TasksTab from "./components/tabs/TasksTab";
import { ConstructionSiteStatusBadge } from "../../component/constructionSites/ConstructionSiteStatusBadge";
import { SkippedBanner } from "./components/SkippedBanner";
import { SkipDialogs } from "./components/SkipDialogs";
import { useConstructionSitePageState } from "./hooks/useConstructionSitePageState";
import { RoleEnum } from "../../../../../packages/vars/src";

const ConstructionSitePage: React.FC = () => {
	const navigate = useNavigate();
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
	});

	const [communicationsSortBy, setCommunicationsSortBy] =
		useState<string>("sentAt");
	const [communicationsSortOrder, setCommunicationsSortOrder] = useState<
		"asc" | "desc"
	>("desc");

	const {
		data: constructionSite,
		isLoading,
		error,
		refetch,
	} = useGetConstructionSiteDetails(constructionSiteId);
	const {
		data: communicationsData,
		isLoading: communicationsLoading,
		error: communicationsError,
	} = useGetConstructionSiteCommunications(constructionSiteId, {
		page: pagination.page,
		limit: pagination.limit,
		sortBy: communicationsSortBy,
		sortOrder: communicationsSortOrder,
	});

	const { data: unprocessedCommunicationsCount = 0 } =
		useGetConstructionSiteUnprocessedCommunicationsCount(constructionSiteId);

	const { data: documentsData } = useGetConstructionSiteDocuments(
		Number(constructionSiteId),
		{ page: 1, limit: 1000 },
	);

	const toProcessDocumentsCount = useMemo(() => {
		if (!documentsData?.data) return 0;
		return documentsData.data.filter((doc) => doc.status === "TO_BE_PROCESSED")
			.length;
	}, [documentsData?.data]);

	const {
		data: tasksData,
		isLoading: tasksLoading,
		error: tasksError,
	} = useGetConstructionSiteTasks(constructionSiteId);

	const todoTasksCount = useMemo(() => {
		if (!tasksData) return 0;
		return tasksData.filter((task) => task.status === "TO_DO").length;
	}, [tasksData]);

	const { me: user } = useMe();
	const isSyndic = user?.role === RoleEnum.SYNDIC;
	const isInsurer = user?.role === RoleEnum.INSURER;

	const tabs: Tab[] = useMemo(() => {
		if (isSyndic) {
			return [
				{ value: "general", label: "Général" },
				{ value: "operation", label: "Opération" },
				{ value: "tarification", label: "Tarification" },
				{ value: "intervenants", label: "Intervenants" },
				{
					value: "documents",
					label: "Documents",
					notificationCount: toProcessDocumentsCount,
				},
				{ value: "taches", label: "Tâches" },
			];
		}
		if (isInsurer) {
			return [
				{ value: "general", label: "Général" },
				{ value: "operation", label: "Opération" },
				{ value: "tarification", label: "Tarification" },
			];
		}
		return [
			{ value: "general", label: "Général" },
			{ value: "operation", label: "Opération" },
			{ value: "tarification", label: "Tarification" },
			{ value: "intervenants", label: "Intervenants" },
			{
				value: "documents",
				label: "Documents",
				notificationCount: toProcessDocumentsCount,
			},
			{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
			{
				value: "communications",
				label: "Communications",
				notificationCount: unprocessedCommunicationsCount,
			},
		];
	}, [
		isSyndic,
		isInsurer,
		toProcessDocumentsCount,
		unprocessedCommunicationsCount,
		todoTasksCount,
	]);

	const defaultTab = "general";
	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault(defaultTab),
	);

	const currentActiveTab = useMemo(() => {
		if (
			isSyndic &&
			![
				"general",
				"operation",
				"tarification",
				"intervenants",
				"documents",
				"taches",
			].includes(activeTab)
		) {
			return "general";
		}
		if (
			isInsurer &&
			!["general", "operation", "tarification"].includes(activeTab)
		) {
			return "general";
		}
		return activeTab;
	}, [isSyndic, isInsurer, activeTab]);

	useConstructionSitePageState({
		entityType: "CONSTRUCTION_SITE",
		entityId: Number(constructionSiteId),
		communications: communicationsData?.data || [],
		tasks: tasksData || [],
		emailRoutingUuid: constructionSite?.emailRoutingUuid || undefined,
		emailPrefix: "chantier",
	});

	const displayReceptionButton = useMemo(() => {
		if (!constructionSite || isInsurer) return false;
		return constructionSite.status === "TO_RECEIVE";
	}, [constructionSite, isInsurer]);

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !constructionSite) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations du chantier."
				tabCount={tabs.length}
			/>
		);
	}

	const handleSubmitConstruction = () => {
		navigate(PAGES.PROJECTS.submission.withParams(constructionSiteId, 1));
	};

	const tabContent: { [key: string]: React.ReactNode } = {
		general: <GeneralTab constructionSite={constructionSite} />,
		operation: <OperationTab constructionSite={constructionSite} />,
		tarification: <TarificationTab constructionSite={constructionSite} />,
		intervenants: <ParticipantsTab constructionSite={constructionSite} />,
		documents: <DocumentsTab constructionSiteId={constructionSiteId} />,
		taches: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des tâches.">
				<TasksTab
					tasks={tasksData || []}
					isLoading={tasksLoading}
					error={tasksError}
				/>
			</ErrorBoundary>
		),
		communications: (
			<CommunicationsTab
				constructionSiteId={constructionSiteId}
				communicationsResponse={communicationsData}
				isLoading={communicationsLoading}
				error={communicationsError}
				onPageChange={(page) => setPagination({ ...pagination, page })}
				onSortChange={(sortBy, sortOrder) => {
					setCommunicationsSortBy(sortBy);
					setCommunicationsSortOrder(sortOrder);
				}}
				onLimitChange={(limit) => setPagination({ ...pagination, limit })}
				emailRoutingUuid={constructionSite?.emailRoutingUuid}
			/>
		),
	};

	const canBeSkipped =
		["NEW", "IN_PROGRESS", "TO_ANALYZE"].includes(constructionSite.status) &&
		!constructionSite.isSkipped;

	return (
		<div className="flex flex-col h-full">
			{constructionSite.isSkipped && (
				<SkippedBanner skipReason={constructionSite.skipReason} />
			)}
			<div className="flex-1">
				<PageLayoutWithTabs
					title={
						<div className="flex items-center gap-2">
							{getConstructionSiteTitle(constructionSite.id)}
							<ConstructionSiteStatusBadge
								status={constructionSite.status}
								isSkipped={constructionSite.isSkipped}
							/>
						</div>
					}
					tabs={tabs}
					activeTab={currentActiveTab}
					onTabChange={setActiveTab}
					tabContent={tabContent}
					topActions={
						<div className="flex items-center justify-between gap-2">
							<Button
								variant="ghost"
								onClick={() => navigate(PAGES.PROJECTS.index)}
								className="has-[>svg]:px-1"
							>
								<ArrowLeft />
								Retour
							</Button>
							<div className="flex items-center gap-2">
								{!isInsurer && (
									<SkipDialogs
										entityId={constructionSiteId}
										isSkipped={constructionSite.isSkipped}
										canBeSkipped={canBeSkipped}
										onSuccess={refetch}
									/>
								)}

								{!isInsurer &&
									!constructionSite.isSkipped &&
									(constructionSite.status === "IN_PROGRESS" ||
										constructionSite.status === "TO_ANALYZE") && (
										<Button
											variant="default"
											onClick={handleSubmitConstruction}
										>
											Soumettre le chantier
											<ArrowRight />
										</Button>
									)}
								{displayReceptionButton && (
									<Button
										variant="default"
										onClick={() =>
											navigate(
												APP_PAGES.PROJECTS.reception.withParams(
													constructionSiteId,
												),
											)
										}
									>
										Receptionner le chantier
										<ArrowRight />
									</Button>
								)}
							</div>
						</div>
					}
				/>
			</div>
		</div>
	);
};

export default ConstructionSitePage;

export const getConstructionSiteTitle = (constructionSiteId: number) => {
	return `CH-${constructionSiteId.toString().padStart(5, "0")}`;
};
